import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import {
  DialogClose,
  QlfConfirmationDialog,
  useAlerts,
  useConfirmationDialog,
} from '@qualifio/ui-commons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { QlfCodeArea } from '@qualifio/ui-commons/dist/components/qlf-code-area';
import {
  ReplaceRulesBody,
  RuleAttributes,
  RuleInput,
  RuleLastModificationInfo,
  RuleType,
} from '@loyalty/shared-common/rule-api-client';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import { useTranslation } from 'react-i18next';
import { Title } from '../../../shared/layout/Title';
import { parseRuleInputsJson } from './rule-import-export-utils';
import { useAccounts } from '../../../accounts/AccountsContext';
import { useApi } from '../../../shared/api-client/ApiClientProvider';
import { useProgram } from '../../../loyalty/ProgramContext';

export interface RuleImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  ruleApiType: RuleType.EARN | RuleType.BURN;
  rulesSnapshot: RuleAttributes[];
  onImported: () => void;
  onConcurrencyConflict: (details: RuleLastModificationInfo) => void;
}

export function RuleImportModal({
  isOpen,
  onClose,
  ruleApiType,
  rulesSnapshot,
  onImported,
  onConcurrencyConflict,
}: RuleImportModalProps): React.ReactElement {
  const { t } = useTranslation();
  const { notify } = useAlerts();
  const { ruleApi } = useApi();
  const { programId } = useProgram();
  const { currentAccount } = useAccounts();

  const { confirmationDialogState, setConfirmationDialogState } = useConfirmationDialog();

  const [jsonContent, setJsonContent] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setJsonContent('');
      setParseError(null);
      setSubmitError(null);
    }
  }, [isOpen]);

  const handleChange = useCallback((value: string | undefined) => {
    const text = value || '';
    setJsonContent(text);
    setSubmitError(null);
    if (!text.trim()) {
      setParseError(null);
      return;
    }
    try {
      const parsed = parseRuleInputsJson(text);
      setParseError(null);
      const legacyAccountId = parsed[0]?.legacyAccountId;
      if (
        !!legacyAccountId &&
        legacyAccountId !== currentAccount.id &&
        text.includes('customKeys')
      ) {
        setParseError(
          'Imported rules should not use criteria with custom attributes from another account. Remove these criteria.',
        );
        return;
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setParseError(message);
    }
  }, []);

  const runReplace = useCallback(async () => {
    let parsedRules: RuleInput[];
    try {
      parsedRules = parseRuleInputsJson(jsonContent);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setSubmitError(message);
      return;
    }

    const oldRules = rulesSnapshot.map(rule => ({
      id: rule.id,
      version: rule.version,
      positionVersion: rule.positionVersion,
    }));

    const body: ReplaceRulesBody = {
      oldRules,
      newRules: parsedRules,
    };

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await ruleApi.replaceRules(programId, ruleApiType, body);
      notify(t('rules.import-export.import.success'), 'success');
      onImported();
      onClose();
    } catch (error: unknown) {
      const axiosError = error as {
        response?: {
          status?: number;
          data?: { details?: RuleLastModificationInfo; message?: string };
        };
      };
      if (axiosError.response?.status === 409 && axiosError.response?.data?.details) {
        onConcurrencyConflict(axiosError.response.data.details);
        onClose();
      } else {
        const message =
          axiosError.response?.data?.message ??
          (error instanceof Error ? error.message : String(error));
        setSubmitError(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    jsonContent,
    ruleApi,
    programId,
    ruleApiType,
    rulesSnapshot,
    notify,
    onImported,
    onClose,
    onConcurrencyConflict,
    t,
  ]);

  const openConfirmReplace = useCallback(() => {
    if (parseError || !jsonContent.trim()) {
      return;
    }
    try {
      parseRuleInputsJson(jsonContent);
    } catch {
      return;
    }

    setConfirmationDialogState(prev => ({
      ...prev,
      open: true,
      color: 'warning',
      title: t('rules.import-export.import.confirm.title'),
      content: t('rules.import-export.import.confirm.description'),
      confirmButtonText: t('rules.import-export.import.confirm.cta'),
      confirmButtonColor: 'secondary',
      onConfirm: () => {
        void runReplace().finally(() =>
          setConfirmationDialogState(innerPrev => ({ ...innerPrev, open: false })),
        );
      },
      onClose: () => setConfirmationDialogState(innerPrev => ({ ...innerPrev, open: false })),
    }));
  }, [jsonContent, parseError, runReplace, setConfirmationDialogState, t]);

  const handleClose = useCallback(() => {
    setParseError(null);
    setSubmitError(null);
    onClose();
  }, [onClose]);

  const parseOk = !parseError && jsonContent.trim().length > 0;

  return (
    <>
      <QlfConfirmationDialog {...confirmationDialogState} />
      <Dialog open={isOpen} onClose={handleClose} maxWidth="xl" fullWidth>
        <DialogClose close={handleClose} />
        <DialogTitle>
          <Title>{t('rules.import-export.import.title')}</Title>
        </DialogTitle>
        <DialogContent>
          {submitError && (
            <Alert severity="error" sx={{ marginBottom: 2 }}>
              {submitError}
            </Alert>
          )}
          <QlfCodeArea
            id="rules-import-json-editor"
            value={jsonContent}
            defaultLanguage="json"
            allowFullScreen
            height="70vh"
            onChange={handleChange}
          />
          {jsonContent.trim() && (
            <Box sx={{ marginTop: 1 }}>
              {parseOk ? (
                <Box sx={{ alignItems: 'center', color: 'success.main', display: 'flex', gap: 1 }}>
                  <Box
                    sx={{
                      alignItems: 'center',
                      backgroundColor: 'success.main',
                      borderRadius: '50%',
                      color: 'common.white',
                      display: 'inline-flex',
                      height: 18,
                      justifyContent: 'center',
                      width: 18,
                    }}
                  >
                    <FontAwesomeIcon icon={faCheck} size="xs" />
                  </Box>
                  <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                    {t('rules.import-export.import.validation.valid', {
                      defaultValue: 'Valid JSON',
                    })}
                  </Typography>
                </Box>
              ) : (
                <Typography component="p" variant="body2" color="error">
                  {parseError}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ paddingX: '40px', justifyContent: 'space-between !important' }}>
          <Button color="grey" onClick={handleClose} startIcon={<FontAwesomeIcon icon={faXmark} />}>
            {t('rules.import-export.import.cancel')}
          </Button>
          <Button
            onClick={openConfirmReplace}
            startIcon={<FontAwesomeIcon icon={faCheck} />}
            disabled={!parseOk || isSubmitting}
            autoFocus
          >
            {t('rules.import-export.import.action')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
